import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { CHANNEL_TYPE } from "@/enums/ai";
import { useAlert } from "@/hooks/useAlert";
import { Assistant as ApiAssistant, AssistantModel } from "@/types/ai";
import { apiLive, apiFetch } from "@/lib/api";
import { GAIL_API } from "@/utils/common/constants/routes";

interface UseAssistantModelUpdateOptions {
   channelType?: CHANNEL_TYPE;
   onModelUpdated?: (assistantId: string, newModel: string) => void;
}

interface UseAssistantModelUpdateReturn {
   updateModel: (assistantId: string, newModel: string) => Promise<boolean>;
   isSaving: boolean;
}

export const useAssistantModelUpdate = (
   options: UseAssistantModelUpdateOptions = {}
): UseAssistantModelUpdateReturn => {
   const { channelType = CHANNEL_TYPE.GPT, onModelUpdated } = options;
   const { setAlert } = useAlert();
   const { t } = useTranslation("assistants");
   const [isSaving, setIsSaving] = useState(false);

   const updateModel = useCallback(
      async (assistantId: string, newModel: string): Promise<boolean> => {
         setIsSaving(true);
         try {
            const assistant = await apiLive<ApiAssistant>(
               `${GAIL_API}/.internal/assistants/${assistantId}`
            );

            if (!assistant) {
               setAlert({
                  status: "error",
                  message: t("modelUpdateFailed"),
                  secondsToClose: 4,
               });
               return false;
            }

            const updatedChannels = assistant.channels.map((channel) =>
               channel.type === channelType
                  ? { ...channel, model: newModel as AssistantModel }
                  : channel
            );

            const {
               id: _id,
               createdAt: _c,
               lastModifiedAt: _l,
               ...payload
            } = assistant;

            const updated = await apiFetch<ApiAssistant>(
              `${GAIL_API}/.internal/assistants/${assistantId}`,
              "PUT",
              {
              body: { ...payload, channels: updatedChannels },
            },
            );

            if (updated) {
               setAlert({
                  status: "success",
                  message: t("modelUpdateSuccess"),
                  secondsToClose: 4,
               });
               onModelUpdated?.(assistantId, newModel);
               return true;
            }

            setAlert({
               status: "error",
               message: t("modelUpdateFailed"),
               secondsToClose: 4,
            });
            return false;
         } catch (error) {
            console.error("Error updating assistant model:", error);
            setAlert({
               status: "error",
               message: t("modelUpdateFailed"),
               secondsToClose: 4,
            });
            return false;
         } finally {
            setIsSaving(false);
         }
      },
      [channelType, onModelUpdated, setAlert, t]
   );

   return { updateModel, isSaving };
};
